import { classifyFailure, createIdempotencyKey } from "@lafayette/api-client";
import type {
  StaffImportApprovalBlocker,
  StaffImportApproveBody,
  StaffImportBulkReviewBody,
  StaffImportJobDetail,
  StaffImportProgress,
  StaffImportRecordDecisionBody,
  StaffImportRetryBody,
  StaffImportReviewBody,
  StaffImportReviewRefusal,
  StaffImportReviewResult,
  StaffImportReviewRowDetail,
} from "@lafayette/schemas";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import { CHANGED_BY_SOMEONE_ELSE } from "../commands";
import type { ImportCommandName } from "../data-provider";
import { describeImportError, type ImportFailure } from "../errors";
import { importKeys, useImportRequest } from "../queries";
import { reviewKeys } from "./queries";

// Runs the review commands (W6 to W10) through the data provider and keeps the caches current. The rules
// are the ones of the CHT-707 commands:
//   1. Idempotency. One user action has one key. After a failure where the same command may run again
//      (no answer, a 503) the next identical attempt reuses the key, so the server runs it once.
//   2. Conflict. On a 409 the progress (it holds the job version) is read again. The caller keeps the
//      choices on the page and says "Changed by someone else".
//   3. The cache. A success marks the progress, the row pages and the row detail stale.

export type ReviewOutcome<T> =
  | Readonly<{ ok: true; data: T }>
  | Readonly<{
      ok: false;
      failure: ImportFailure;
      /** The server refused because the job changed under this tab (a 409). */
      conflict: boolean;
      /** W6 and W7: which rows the server refused, by id and code. */
      refusals: readonly StaffImportReviewRefusal[];
      /** W9: why the job cannot be approved. */
      blockers: readonly StaffImportApprovalBlocker[];
    }>;

export type ReviewCommands = ReturnType<typeof useReviewCommands>;

export function useReviewCommands(jobId: string) {
  const request = useImportRequest();
  const queryClient = useQueryClient();
  const pendingKey = useRef<{ signature: string; key: string } | null>(null);
  const [pendingCount, setPendingCount] = useState(0);

  // `keepRow`: the answer of the command already holds the row, so it is not read again. Each read counts
  // against the rate limit of the import routes.
  const refresh = useCallback(
    async (keepRow: boolean) => {
      await queryClient.invalidateQueries({ queryKey: reviewKeys.progress(jobId) });
      void queryClient.invalidateQueries({ queryKey: reviewKeys.rowsOf(jobId) });
      if (!keepRow) {
        void queryClient.invalidateQueries({ queryKey: ["imports", "review-row", jobId] });
      }
    },
    [jobId, queryClient],
  );

  const run = useCallback(
    async <T>(
      command: ImportCommandName,
      payload: unknown,
      onSuccess?: (data: T) => void,
      keepRow = false,
    ): Promise<ReviewOutcome<T>> => {
      const signature = JSON.stringify([command, jobId, payload]);
      const key =
        pendingKey.current?.signature === signature
          ? pendingKey.current.key
          : createIdempotencyKey();
      pendingKey.current = { signature, key };

      setPendingCount((count) => count + 1);
      try {
        const data = await request<T>({ command, jobId, idempotencyKey: key }, payload);
        pendingKey.current = null;
        onSuccess?.(data);
        await refresh(keepRow);
        return { ok: true, data };
      } catch (error) {
        const failure = describeImportError(error);
        if (!failure.retryable) pendingKey.current = null;
        const refusal = classifyFailure(error).detail.importRefusal;
        const conflict = failure.kind === "conflict";
        if (conflict) await refresh(false);
        return {
          ok: false,
          failure,
          conflict,
          refusals: refusal?.reviewRefusals ?? [],
          blockers: refusal?.approvalBlockers ?? [],
        };
      } finally {
        setPendingCount((count) => count - 1);
      }
    },
    [jobId, refresh, request],
  );

  return {
    pending: pendingCount > 0,
    /** The job version of the last read of R7, for the next write. `null` before the first read. */
    jobVersion: (): number | null =>
      queryClient.getQueryData<StaffImportProgress>(reviewKeys.progress(jobId))?.job.version ??
      null,
    /** W4 on a validated job with nothing left to import: closes it. */
    close: (expectedVersion: number) =>
      run<StaffImportJobDetail>("cancelJob", { expectedVersion, reason: "No changes to import" }),
    /** W6. The answer is the updated row; it is written to the row cache. */
    decide: (rowId: string, body: StaffImportRecordDecisionBody) =>
      run<StaffImportReviewRowDetail>(
        "recordDecision",
        { rowId, body },
        (detail) => queryClient.setQueryData(reviewKeys.row(jobId, rowId), detail),
        true,
      ),
    /** W7. */
    review: (body: StaffImportReviewBody) => run<StaffImportReviewResult>("reviewRows", body),
    /** W8. */
    bulk: (body: StaffImportBulkReviewBody) => run<StaffImportReviewResult>("bulkReview", body),
    /** W9. */
    approve: (body: StaffImportApproveBody) =>
      run<StaffImportJobDetail>(
        "approveJob",
        body,
        () => void queryClient.invalidateQueries({ queryKey: importKeys.lists() }),
      ),
    /** W10. The answer is the NEW job. */
    retry: (body: StaffImportRetryBody) =>
      run<StaffImportJobDetail>(
        "retryJob",
        body,
        () => void queryClient.invalidateQueries({ queryKey: importKeys.lists() }),
      ),
  };
}

/** The sentence under a failed review command. A conflict says so in plain words. */
export function describeReviewFailure(
  outcome: Extract<ReviewOutcome<unknown>, { ok: false }>,
): string {
  if (outcome.conflict) {
    return `${CHANGED_BY_SOMEONE_ELSE}. This import was reviewed, approved or closed by another person. Your choices on this page are still here. Check them and try again.`;
  }
  return outcome.failure.message;
}
